import ColorPicker from '@stimulus-components/color-picker'
import Pickr from "@simonwep/pickr"
import chroma from 'chroma-js'

// Luminance thresholds — keep in sync with ColorContrast concern (Ruby)
const LIGHT_TEXT_LUMINANCE_THRESHOLD = 0.5
const LIGHT_PATTERN_LUMINANCE_THRESHOLD = 0.8

export default class extends ColorPicker {
  static targets = ['input', 'button', 'display', 'textColorInput']
  static values = { colorVar: String, textColorVar: String, autoAdjust: Boolean }

  // overwriting the default connect to customize the position and add change event listeners
  connect() {
    this.picker = Pickr.create({
      el: this.buttonTarget,
      theme: this.themeValue,
      default: this.inputTarget.value,
      swatches: this.swatches,
      components: this.componentOptions,
      position: "right",
      comparison: false
    })

    // Update display after parent connection
    this.initialColor = this.inputTarget.value
    this.currentColor = this.initialColor
    this.updateDisplay(this.inputTarget.value)

    // Use requestAnimationFrame to throttle updates to browser repaint cycle
    let rafId = null

    this.picker.on('change', (color) => {
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          this.updateDisplay(color.toHEXA().toString())
          rafId = null
        })
      }
    })

    this.picker.on("hide", () => this.autoAdjust())
  }

  autoAdjust() {
    // If auto-adjust is enabled, adjust the color to ensure sufficient contrast to read the black text
    if (this.autoAdjustValue) {
      let color = this.currentColor
      const chromaColor = chroma(color)
      const luminance = chromaColor.luminance()

      // If the color is too dark for black text, lighten it
      if (luminance < LIGHT_TEXT_LUMINANCE_THRESHOLD) {
        // Target luminance for good contrast with black text
        color = chromaColor.luminance(0.65).hex()

        // Update picker to show the adjusted color
        this.picker.setColor(color)
        this.updateDisplay(color)
      }
    }
  }

  updateDisplay(colorHex) {
    this.currentColor = colorHex
    this.inputTarget.value = colorHex
    this.inputTarget.dispatchEvent(new Event('change'))
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = colorHex
    }
    document.documentElement.style.setProperty(this.colorVarValue, colorHex)

    // Calculate contrast and set text color for readability
    // Use luminance: light colors (>0.5) need black text, dark colors need white text
    if (this.hasTextColorVarValue) {
      const color = chroma(colorHex)
      const luminance = color.luminance()

      const textColor = luminance > LIGHT_TEXT_LUMINANCE_THRESHOLD ? "#000" : "#fff"
      document.documentElement.style.setProperty(this.textColorVarValue, textColor)

      // Update the hidden form field with the calculated text color
      if (this.hasTextColorInputTarget) {
        this.textColorInputTarget.value = textColor
      }

      // Set name color for names/logo on white card background.
      const nameColor = luminance > LIGHT_TEXT_LUMINANCE_THRESHOLD ? '#000000' : colorHex
      document.documentElement.style.setProperty('--cover-name-color', nameColor)

      // Set pattern color — only switch to gray for very light covers.
      const patternColor = luminance > LIGHT_PATTERN_LUMINANCE_THRESHOLD ? '#CCCCCC' : '#FFFFFF'
      document.documentElement.style.setProperty('--cover-pattern-color', patternColor)
    }
  }

  setColor(colorHex) {
    this.updateDisplay(colorHex)
    // Update the picker instance if it exists
    if (this.picker) {
      this.picker.setColor(colorHex)
    }
  }

  get componentOptions() {
    return {
      hue: true,

      interaction: {
        input: true,
        clear: false,
        save: false,
        cancel: false,
      },
    }
  }
}
